import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { useCatalog } from "@/context/CatalogContext";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/track";
import { Plus, BellRing, Search, ArrowUpDown, Flame, ShieldCheck } from "lucide-react";
import RestockDialog from "@/components/RestockDialog";
import FreeShipBanner from "@/components/FreeShipBanner";
import { useStock } from "@/context/StockContext";
import { useResearchIndex } from "@/hooks/useResearchIndex";

export function ProductCard({ p, index, verified, featured }) {
  const { addItem } = useCart();
  const { isSoldOut, quantityOf } = useStock();
  const [restock, setRestock] = useState(false);
  const soldOut = isSoldOut(p);
  const qty = quantityOf(p);
  const low = !soldOut && qty !== null && qty !== undefined && qty > 0 && qty <= 5;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.45, delay: (index % 4) * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative border border-zinc-800 hover:border-[#D4AF37]/50 bg-[#0C0C0C] transition-colors duration-500 flex flex-col ${soldOut ? "opacity-70" : ""}`}
      data-testid={`product-card-${p.id}`}>
      <Link to={`/product/${p.id}`} className="relative overflow-hidden bg-black aspect-square block" data-testid={`product-link-${p.id}`}>
        <img src={p.image} alt={p.name} loading="lazy" className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out ${soldOut ? "opacity-40 grayscale" : ""}`} />
        <span className="absolute top-3 left-3 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-[#D4AF37] bg-black/65 backdrop-blur px-2 py-0.5">{p.size}</span>
        {(featured || p.bestseller) && !soldOut && (
          <span className="absolute bottom-3 left-3 flex items-center gap-1 font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-black bg-[#D4AF37] px-2 py-0.5" data-testid={`bestseller-${p.id}`}>
            <Flame size={10} className="fill-black" /> Featured
          </span>
        )}
        {soldOut && <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.18em] text-red-400 border border-red-400/40 bg-black/60 backdrop-blur px-1.5 py-0.5" data-testid={`sold-out-${p.id}`}>Sold Out</span>}
        {low && <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.18em] text-amber-400 border border-amber-400/40 bg-black/60 backdrop-blur px-1.5 py-0.5" data-testid={`low-stock-${p.id}`}>Only {qty} left</span>}
      </Link>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <Link to={`/product/${p.id}`} className="font-serif text-lg md:text-xl font-light leading-tight hover:text-[#D4AF37] transition-colors line-clamp-2">{p.name}</Link>
        <div className="flex flex-wrap items-center gap-1.5 mt-2 mb-4" data-testid={`product-meta-${p.id}`}>
          {p.category && <span className="font-mono text-[8px] uppercase tracking-[0.12em] text-[#71717A] border border-zinc-800 px-1.5 py-0.5">{p.category}</span>}
          {verified && <span className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.12em] text-emerald-400 border border-emerald-400/30 bg-emerald-400/[0.06] px-1.5 py-0.5" data-testid={`verified-badge-${p.id}`}><ShieldCheck size={10} /> Verified</span>}
        </div>
        <div className="flex items-center justify-between gap-2 mt-auto">
          <span className="font-serif text-xl md:text-2xl">${p.price}</span>
          {soldOut ? (
            <button onClick={() => setRestock(true)} data-testid={`notify-restock-${p.id}`} aria-label={`Notify me when ${p.name} restocks`}
              className="flex items-center gap-1.5 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-colors">
              <BellRing size={12} /> Notify
            </button>
          ) : (
            <button onClick={() => addItem(p)} data-testid={`add-to-cart-${p.id}`} aria-label={`Add ${p.name} to cart`}
              className="flex items-center gap-1.5 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-colors">
              <Plus size={12} /> Add
            </button>
          )}
        </div>
      </div>
      <RestockDialog product={p} open={restock} onOpenChange={setRestock} />
    </motion.div>
  );
}

const SORTS = [
  { id: "featured", label: "Featured first" },
  { id: "az", label: "Name: A–Z" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
];

const CATEGORIES = [
  { id: "All", label: "All" },
  { id: "Metabolic", label: "Metabolic" },
  { id: "Growth", label: "Growth & Recovery" },
  { id: "Longevity", label: "Longevity & Cellular" },
  { id: "Specialty", label: "Specialty" },
];

function sortProducts(list, sort) {
  const arr = [...list];
  switch (sort) {
    case "az": return arr.sort((a, b) => a.name.localeCompare(b.name));
    case "price-asc": return arr.sort((a, b) => a.price - b.price);
    case "price-desc": return arr.sort((a, b) => b.price - a.price);
    default: return arr.sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0));
  }
}

export default function ProductGrid({ standalone = false }) {
  const { products, loading } = useCatalog();
  const { isSoldOut } = useStock();
  const { byProduct } = useResearchIndex();
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [cat, setCat] = useState(params.get("cat") || "All");
  const [sort, setSort] = useState("featured");
  const q = query.trim().toLowerCase();

  useEffect(() => {
    if (q.length < 2) return;
    const t = setTimeout(() => trackEvent("search_used", { q: q.slice(0, 60) }), 900);
    return () => clearTimeout(t);
  }, [q]);

  let filtered = products.filter((p) => !isSoldOut(p));
  if (cat !== "All") filtered = filtered.filter((p) => (p.category || "").startsWith(cat));
  if (q) filtered = filtered.filter((p) => (p.name + " " + p.blurb + " " + (p.sku || "")).toLowerCase().includes(q));
  const available = sortProducts(filtered, sort);

  return (
    <section id="catalog" className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-24 md:pb-32" data-testid="catalog">
      <div className="mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Research Catalog</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-none mb-3">{products.filter((p) => !isSoldOut(p)).length} Research Compounds</h1>
        <p className="text-[#71717A] text-sm" data-testid="catalog-availability-line">{products.filter((p) => !isSoldOut(p)).length} compounds available now</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search compounds, SKUs…" data-testid="catalog-search"
            autoFocus={standalone && params.get("focus") === "1"}
            className="w-full bg-transparent border border-zinc-800 focus:border-[#D4AF37] outline-none pl-11 pr-4 py-3 font-mono text-sm placeholder:text-[#52525B] transition-colors" />
        </div>
        <div className="relative sm:w-56">
          <ArrowUpDown size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B] pointer-events-none" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort" aria-label="Sort products"
            className="w-full appearance-none bg-[#0C0C0C] border border-zinc-800 focus:border-[#D4AF37] outline-none pl-11 pr-4 py-3 font-mono text-xs uppercase tracking-[0.12em] text-[#A1A1AA] transition-colors cursor-pointer">
            {SORTS.map((s) => <option key={s.id} value={s.id} className="bg-[#0C0C0C]">{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="catalog-filters">
        {CATEGORIES.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)} data-testid={`catalog-filter-${c.id.toLowerCase()}`} aria-pressed={cat === c.id}
            className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${cat === c.id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-800 text-[#A1A1AA] hover:border-[#D4AF37]/50"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <FreeShipBanner variant="bar" className="mb-8 max-w-md" />
      {available.length === 0 && <p className="font-mono text-sm text-[#52525B] py-10" data-testid="catalog-no-results">{loading ? "Loading catalog…" : `No compounds match "${query}".`}</p>}

      {available.length > 0 && (
        <>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-4" data-testid="available-now-heading">Available Now ({available.length})</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px mb-12">
            {available.map((p, i) => <ProductCard key={p.id} p={p} index={i} verified={!!byProduct[p.id]?.identityVerified} />)}
          </div>
        </>
      )}
    </section>
  );
}
